import React, { useState, useEffect } from 'react';
import { FaCreditCard, FaShieldAlt, FaCheck, FaExclamationTriangle, FaClock, FaTruck, FaReceipt, FaArrowLeft } from 'react-icons/fa';
import Badge from '@/components/common/Badge';
import Card, { CardBody } from '@/components/common/Card';
import Button from '@/components/common/Button';
import Modal from '@/components/common/Modal';
import { useUser } from '@clerk/clerk-react';
import toastService from '@/utils/toastService';

/**
 * Mock del proceso de pago integrado con MercadoPago
 * Maneja pagos mixtos (repuestos propios + teknoParts)
 */
const PagoRepuestosExternos = ({ 
  isOpen, 
  onClose, 
  datosCarrito = null,
  onPagoCompletado,
  onVolverCarrito 
}) => {
  const { user } = useUser();
  const [paso, setPaso] = useState(1); // 1: Resumen, 2: Pago, 3: Confirmación
  const [loading, setLoading] = useState(false);
  const [metodoPago, setMetodoPago] = useState('mercadopago');
  const [datosPago, setDatosPago] = useState(null);
  const [ordenCreada, setOrdenCreada] = useState(null);

  // Mock de datos del taller
  const datosUsuario = {
    nombre: user?.fullName || 'Taller San Martín',
    email: user?.emailAddresses?.[0]?.emailAddress || 'contacto@tallersanmartin.cl',
    rut: '76.123.456-7',
    direccion: 'Av. Providencia 1234, Santiago',
    telefono: '+56 9 8765 4321'
  };

  const resetearEstado = () => {
    setPaso(1);
    setLoading(false);
    setDatosPago(null);
    setOrdenCreada(null);
  };

  useEffect(() => {
    if (isOpen) {
      resetearEstado();
    }
  }, [isOpen]);

  if (!datosCarrito || !datosCarrito.items) {
    return null;
  }

  const { items, totales } = datosCarrito;

  const handleProcesarPago = async () => {
    setLoading(true);
    try {
      // Simular creación de preferencia de MercadoPago
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const mockPreferencia = {
        id: `PREF-${Date.now()}`,
        init_point: 'https://www.mercadopago.cl/checkout/v1/redirect?pref_id=123456789',
        sandbox_init_point: 'https://sandbox.mercadopago.cl/checkout/v1/redirect?pref_id=123456789',
        collector_id: 123456789,
        operation_type: 'regular_payment',
        date_created: new Date().toISOString(),
        expires: true,
        expiration_date_from: new Date().toISOString(),
        expiration_date_to: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString()
      };

      setDatosPago(mockPreferencia);
      setPaso(2);
      
      // Simular apertura de ventana de MercadoPago
      setTimeout(() => {
        simularPagoExitoso();
      }, 3000);
      
    } catch (error) {
      toastService.error('Error al procesar el pago');
      console.error('Error en pago:', error);
    } finally {
      setLoading(false);
    }
  };

  const simularPagoExitoso = async () => {
    setLoading(true);
    try {
      // Simular webhook de MercadoPago
      const mockOrden = {
        id: `ORD-${Date.now()}`,
        external_reference: `MANT-${datosCarrito.mantenimientoId || 'NEW'}`,
        status: 'approved',
        status_detail: 'accredited',
        payment_id: `PAY-${Date.now()}`,
        payment_type: 'credit_card',
        merchant_order_id: `MO-${Date.now()}`,
        preference_id: datosPago?.id,
        transaction_amount: totales.total,
        date_approved: new Date().toISOString(),
        date_created: new Date().toISOString(),
        money_release_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
        payer: {
          email: datosUsuario.email,
          identification: {
            type: 'RUT',
            number: datosUsuario.rut
          }
        },
        // Información específica de repuestos teknoParts
        repuestos_info: {
          propios: items.filter(item => !item.esExterno).length,
          teknoParts: items.filter(item => item.esExterno).length,
          ordenes_proveedor: items
            .filter(item => item.esExterno)
            .map(item => ({
              repuesto_id: item.id,
              proveedor_id: item.proveedor?.id,
              cantidad: item.cantidad,
              precio_proveedor: item.precioProveedor,
              orden_shopify_id: `SHOP-${Date.now()}-${item.id}`,
              tiempo_entrega_estimado: item.tiempoEntrega
            }))
        }
      };

      setOrdenCreada(mockOrden);
      setPaso(3);
      
      toastService.success('¡Pago procesado exitosamente!');
      
    } catch (error) {
      toastService.error('Error al confirmar el pago');
    } finally {
      setLoading(false);
    }
  };

  const handleCerrarModal = () => {
    if (paso === 3 && ordenCreada) {
      // Pago completado exitosamente
      onPagoCompletado(ordenCreada);
    }
    onClose();
  };

  const renderPaso1 = () => (
    <>
      {/* Columna 1: Resumen del pedido e Información de Entrega */}
      <div className="flex-1 flex flex-col space-y-6">
        <div>
          
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {items.map(item => (
              <div key={item.id} className="p-3 bg-gray-700 rounded border border-gray-600">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <img 
                      src={item.imagen} 
                      alt={item.nombre}
                      className="w-12 h-12 object-cover rounded flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm sm:text-base truncate">{item.nombre}</p>
                      <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-400 mt-1">
                        <span>Cantidad: {item.cantidad}</span>
                        <Badge 
                          variant={item.esExterno ? 'info' : 'success'}
                          size="xs"
                        >
                          {item.esExterno ? 'Externo' : 'Propio'}
                        </Badge>
                        {item.esExterno && item.tiempoEntrega > 0 && (
                          <Badge variant="warning" size="xs">
                            <FaClock className="mr-1" />
                            {item.tiempoEntrega} día{item.tiempoEntrega > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-left sm:text-right flex-shrink-0">
                    <p className="font-semibold text-lg">${(item.precio * item.cantidad).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</p>
                    <p className="text-xs sm:text-sm text-gray-400">${item.precio.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')} c/u</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>


        {/* Datos de facturación - Solo paso 1 */}
        {paso === 1 && (
          <Card className="bg-gray-700 border border-gray-600">
            <CardBody className="p-4">
              <h4 className="font-semibold mb-3">Datos de Facturación</h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">Taller:</span>
                  <p className="font-medium">{datosUsuario.nombre}</p>
                </div>
                <div>
                  <span className="text-gray-400">RUT:</span>
                  <p className="font-medium">{datosUsuario.rut}</p>
                </div>
                <div>
                  <span className="text-gray-400">Email:</span>
                  <p className="font-medium">{datosUsuario.email}</p>
                </div>
                <div>
                  <span className="text-gray-400">Teléfono:</span>
                  <p className="font-medium">{datosUsuario.telefono}</p>
                </div>
                <div className="col-span-2">
                  <span className="text-gray-400">Dirección:</span>
                  <p className="font-medium">{datosUsuario.direccion}</p>
                </div>
              </div>
            </CardBody>
          </Card>
        )}
      </div>

      {/* Columna 2: Totales, Método de Pago y Botones */}
      <div className="w-full lg:w-80 flex flex-col space-y-6">
        {/* Totales */}
        <Card className="bg-gray-700 border border-gray-600">
          <CardBody className="p-4">
            <h4 className="font-semibold mb-3">Detalle de Pago</h4>
            <div className="space-y-2 text-sm">
              {totales.itemsPropios > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Repuestos propios ({totales.itemsPropios}):</span>
                  <span>${totales.propios.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                </div>
              )}
              {totales.itemsExternos > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-400">Repuestos teknoParts ({totales.itemsExternos}):</span>
                  <span>${totales.teknoParts.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                </div>
              )}
              <div className="border-t border-gray-600 pt-2 mt-3">
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total a Pagar:</span>
                  <span className="text-green-400">${totales.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}</span>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Método de pago */}
        <Card className="bg-gray-700 border border-gray-600">
          <CardBody className="p-4">
            <h4 className="font-semibold mb-3">Método de Pago</h4>
            <div className="space-y-3">
              <label className="flex items-center p-3 border border-gray-600 rounded cursor-pointer hover:border-blue-500 transition-colors">
                <input
                  type="radio"
                  name="metodoPago"
                  value="mercadopago"
                  checked={metodoPago === 'mercadopago'}
                  onChange={(e) => setMetodoPago(e.target.value)}
                  className="mr-3"
                />
                <div className="flex items-center">
                  <img 
                    src="/api/placeholder/40/24" 
                    alt="MercadoPago"
                    className="mr-3"
                  />
                  <div>
                    <p className="font-medium">MercadoPago</p>
                    <p className="text-xs text-gray-400">Tarjetas, transferencia o saldo MP</p>
                  </div>
                </div>
                <div className="ml-auto">
                  <FaShieldAlt className="text-green-400" />
                </div>
              </label>
            </div>
          </CardBody>
        </Card>

        {/* Botones */}
        <div className="flex flex-col gap-3 mt-8 mb-12">
          <Button
            variant="primary"
            onClick={handleProcesarPago}
            loading={loading}
            className="w-full"
            icon={<FaCreditCard />}
          >
            Proceder al Pago
          </Button>
          
          <Button
            variant="secondary"
            onClick={onVolverCarrito || onClose}
            className="w-full"
            icon={<FaArrowLeft />}
          >
            Volver al Carrito
          </Button>
        </div>
      </div>
    </>
  );

  const renderPaso2 = () => (
    <div className="text-center py-8">
      <div className="mb-6">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <h3 className="text-xl font-semibold mb-2">Procesando Pago</h3>
        <p className="text-gray-400">Redirigiendo a MercadoPago...</p>
      </div>

      <Card className="bg-blue-900/20 border-blue-800/50 max-w-md mx-auto">
        <CardBody className="p-4">
          <div className="flex items-center justify-center mb-2">
            <FaShieldAlt className="text-blue-400 mr-2" />
            <span className="text-blue-200 font-medium">Pago Seguro</span>
          </div>
          <p className="text-sm text-blue-300">
            Tu pago está protegido por MercadoPago
          </p>
        </CardBody>
      </Card>

      <div className="mt-6 text-sm text-gray-400">
        <p>Simulando proceso de pago...</p>
        <p className="mt-1">En producción serías redirigido a MercadoPago</p>
      </div>
    </div>
  );

  const renderPaso3 = () => (
    <div className="text-center py-6">
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
          <FaCheck className="text-white text-2xl" />
        </div>
        <h3 className="text-2xl font-semibold text-green-400 mb-2">¡Pago Exitoso!</h3>
        <p className="text-gray-400">Tu pedido ha sido procesado correctamente</p>
      </div>

      {ordenCreada && (
        <div className="space-y-4">
          {/* Información de la orden */}
          <Card className="bg-gray-800">
            <CardBody className="p-4">
              <h4 className="font-semibold mb-3">Detalles de la Transacción</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400">ID de Orden:</span>
                  <p className="font-medium">{ordenCreada.id}</p>
                </div>
                <div>
                  <span className="text-gray-400">ID de Pago:</span>
                  <p className="font-medium">{ordenCreada.payment_id}</p>
                </div>
                <div>
                  <span className="text-gray-400">Monto Pagado:</span>
                  <p className="font-medium text-green-400">
                    ${ordenCreada.transaction_amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  </p>
                </div>
                <div>
                  <span className="text-gray-400">Fecha:</span>
                  <p className="font-medium">
                    {new Date(ordenCreada.date_approved).toLocaleDateString('es-CL')}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Estado de repuestos teknoParts */}
          {ordenCreada.repuestos_info.teknoParts > 0 && (
            <Card className="bg-blue-900/20 border-blue-800/50">
              <CardBody className="p-4">
                <div className="flex items-center mb-2">
                  <FaTruck className="text-blue-400 mr-2" />
                  <h4 className="font-medium text-blue-200">Estado de Repuestos Externos</h4>
                </div>
                <p className="text-sm text-blue-300 mb-3">
                  Se han creado automáticamente {ordenCreada.repuestos_info.teknoParts} orden{ordenCreada.repuestos_info.teknoParts > 1 ? 'es' : ''} 
                  de compra con el proveedor
                </p>
                
                <div className="space-y-2">
                  {ordenCreada.repuestos_info.ordenes_proveedor.map((orden, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-gray-800 rounded text-xs">
                      <span>Orden #{orden.orden_shopify_id}</span>
                      <Badge variant="warning" size="xs">
                        <FaClock className="mr-1" />
                        {orden.tiempo_entrega_estimado} día{orden.tiempo_entrega_estimado > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {/* Próximos pasos */}
          <Card className="bg-yellow-900/20 border-yellow-800/50">
            <CardBody className="p-4">
              <div className="flex items-center mb-2">
                <FaReceipt className="text-yellow-400 mr-2" />
                <h4 className="font-medium text-yellow-200">Próximos Pasos</h4>
              </div>
              <div className="text-sm text-yellow-300 space-y-2">
                <p>• Recibirás un email de confirmación en {datosUsuario.email}</p>
                {ordenCreada.repuestos_info.propios > 0 && (
                  <p>• Los repuestos propios están disponibles inmediatamente en tu inventario</p>
                )}
                {ordenCreada.repuestos_info.teknoParts > 0 && (
                  <p>• Te notificaremos cuando los repuestos teknoParts estén en camino</p>
                )}
                <p>• Puedes hacer seguimiento desde el panel de órdenes</p>
              </div>
            </CardBody>
          </Card>
        </div>
      )}

      <div className="mt-6 mb-4 flex justify-center">
        <Button
          variant="primary"
          onClick={handleCerrarModal}
          icon={<FaCheck />}
        >
          Continuar
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCerrarModal}
      title={
        paso === 1 ? 'Confirmar Pago' :
        paso === 2 ? 'Procesando Pago' :
        'Pago Completado'
      }
      size={paso === 1 ? "6xl" : "xl"}
      className={paso === 1 ? "w-full lg:max-w-6xl" : ""}
      headerExtra={
        <div className="flex items-center gap-2">
          <Badge variant={paso === 3 ? 'success' : 'primary'} size="sm">
            Paso {paso} de 3
          </Badge>
          {paso === 1 && (
            <Badge variant="secondary" size="sm">
              ${totales.total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
            </Badge>
          )}
        </div>
      }
    >
      <div className={paso === 1 ? "flex flex-col lg:flex-row gap-6 max-h-[80vh]" : ""}>
        {paso === 1 && renderPaso1()}
        {paso === 2 && renderPaso2()}
        {paso === 3 && renderPaso3()}
      </div>
    </Modal>
  );
};

export default PagoRepuestosExternos;